import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Snackbar, Paper, Typography, Box, IconButton } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from '../../Redux/hooks';
import { store as reduxStore } from '../../Redux/store';
import { usePermission } from '../../Redux/SlicesHooks/usePermission';

interface OrderToastData {
  orderId: string | number;
  orderCode?: string;
  storeId?: string | number;
  storeName?: string;
}

const TOAST_EVENT = 'order-notification-toast';
const USER_NOTIFICATION_ROLES = new Set([
  'Super Admin',
  'Super User',
  'Support Admin',
  'Fleet Admin',
]);

export const triggerOrderToast = (data: OrderToastData) => {
  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: data }));
};

const OrderNotificationToast: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [toastData, setToastData] = useState<OrderToastData | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canViewNotification = usePermission('notification', 'view');
  const roleName = useAppSelector((state) => state?.roleAndPermission?.name) || '';
  const notificationScope = useAppSelector((state: any) => state?.roleAndPermission?.notification_scope);
  const canShowOrderToast =
    canViewNotification ||
    notificationScope === 'user' ||
    USER_NOTIFICATION_ROLES.has(roleName);

  const autoCloseSec = useAppSelector(
    (state) => state.orderNotificationSettings?.toastAutoCloseSec ?? 5
  );

  const handleToastEvent = useCallback((e: Event) => {
    // Skip showing toast if notification.view permission is false
    if (!canShowOrderToast) {
      return;
    }
    const detail = (e as CustomEvent<OrderToastData>).detail;
    setToastData(detail);
    setOpen(true);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, autoCloseSec * 1000);
  }, [autoCloseSec, canShowOrderToast]);

  useEffect(() => {
    window.addEventListener(TOAST_EVENT, handleToastEvent);
    return () => {
      window.removeEventListener(TOAST_EVENT, handleToastEvent);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [handleToastEvent]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  const handleClick = () => {
    setOpen(false);
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!toastData) return;

    const state = reduxStore.getState();
    const storeId =
      toastData.storeId ||
      localStorage.getItem('notification_store_id') ||
      (state.store as any)?.storeID ||
      localStorage.getItem('CURRENT_STORE_ID');

    if (storeId && Number(storeId) > 0) {
      window.location.href = `/stores/${storeId}/orders#new`;
    } else {
      window.location.href = '/dashboard/orders';
    }
  };

  const displayId = toastData?.orderCode || toastData?.orderId || '';
  const displayTitle = `New Order #${displayId}${toastData?.storeName ? ` - ${toastData.storeName}` : ''}`;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={handleClose}
      sx={{ mt: 7 }}
    >
      <Paper
        elevation={6}
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          cursor: 'pointer',
          borderLeft: '4px solid #2e7d32',
          minWidth: 300,
          maxWidth: 420,
          '&:hover': { backgroundColor: '#f5f5f5' },
        }}
      >
        <ShoppingCartIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap title={displayTitle}>
            {displayTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            Click to open order
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          sx={{ ml: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

export default OrderNotificationToast;
